import { FC, JSX } from "react";
import { Box, AspectRatio } from "@radix-ui/themes";
import useViewportWidth from "@/hooks/useViewportWidth";
import { Profile } from "@/models/Profile.model";

/**
 * A functional component that renders a profile cover photo with a responsive aspect ratio.
 * The aspect ratio adjusts based on the viewport width (mobile, tablet, or desktop).
 * If a `coverURL` is provided, it displays the image; otherwise, it renders a placeholder box.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {string} props.coverURL - The URL of the cover photo to display.
 * @returns {JSX.Element} The rendered profile cover photo component.
 */

const ProfileCoverPhoto: FC<Partial<Profile>> = ({ coverURL }): JSX.Element => {
  const isMobile = useViewportWidth(768);
  const isTablet = useViewportWidth(1280);

  const imageURL = isMobile
    ? coverURL + "&tr=w-770,h-384,q-70"
    : isTablet
    ? coverURL + "&tr=w-960,h-384,q-70"
    : coverURL + "&tr=w-1920,h-384,q-70";

  return (
    <AspectRatio ratio={isMobile ? 2 / 1 : isTablet ? 5 / 2 : 5 / 1}>
      {coverURL ? (
        <img
          src={imageURL}
          alt="A house in a forest"
          style={{
            objectFit: "cover",
            width: "100%",
            height: "100%",
            backgroundColor: "var(--accent-3)",
          }}
        />
      ) : (
        <Box
          style={{
            objectFit: "cover",
            width: "100%",
            height: "100%",
            backgroundColor: "var(--accent-3)",
          }}
        />
      )}
    </AspectRatio>
  );
};

export default ProfileCoverPhoto;
